import { CanalDenuncias } from './pages/public/canal-denuncias/canal-denuncias';
import { Routes } from '@angular/router';
import { platformAuthGuard } from './core/auth/platform-auth.guard';
import { tenantAuthGuard } from './core/auth/tenant-auth.guard';
import { criticalCaseGuard } from './core/auth/critical-case.guard';
import { permissionGuard } from './core/auth/permission.guard';
import { Layout } from './layout/layout';
import { AcceptInvite } from './pages/auth/accept-invite/accept-invite';
import { Login } from './pages/auth/login/login';
import { RequestAccess } from './pages/auth/request-access/request-access';
import { Dashboard } from './pages/dashboard/dashboard';
import { OccurrenceDetail } from './pages/occurrences/occurrence-detail/occurrence-detail';
import { OccurrenceForm } from './pages/occurrences/occurrence-form/occurrence-form';
import { OccurrenceList } from './pages/occurrences/occurrence-list/occurrence-list';
import { OccurrenceStatistics } from './pages/occurrences/occurrence-statistics/occurrence-statistics';
import { CriticalCaseList } from './pages/critical-cases/critical-case-list/critical-case-list';
import { CriticalCaseDetail } from './pages/critical-cases/critical-case-detail/critical-case-detail';
import { ClientList } from './pages/clients/client-list/client-list';
import { ClientForm } from './pages/clients/client-form/client-form';
import { ClientDetail } from './pages/clients/client-detail/client-detail';
import { OrganizationList } from './pages/organizations/organization-list/organization-list';
import { OrganizationForm } from './pages/organizations/organization-form/organization-form';
import { OrganizationDetail } from './pages/organizations/organization-detail/organization-detail';
import { AccessRequests } from './pages/platform/access-requests/access-requests';
import { OccurrenceConversation } from './pages/public/occurrence-conversation/occurrence-conversation';
import { OccurrencePublicForm } from './pages/public/occurrence-public-form/occurrence-public-form';
import { OccurrencePublicTracking } from './pages/public/occurrence-public-tracking/occurrence-public-tracking';
import { MailTemplates } from './pages/settings/mail-templates/mail-templates';
import { ModuleMailboxes } from './pages/settings/module-mailboxes/module-mailboxes';
import { PublicPortal } from './pages/settings/public-portal/public-portal';
import { Settings } from './pages/settings/settings';
import { UserList } from './pages/settings/user-list/user-list';

// Módulos ativos: ocorrências (núcleo) + helpdesk (chamados, clientes, organizações) e o
// dashboard que os resume. Grupos, macros, listas, SLA e automações continuam no disco,
// fora das rotas — para os reativar, basta voltar a declará-los aqui.
export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },

  // Público — porta única de entrada: o e-mail é que resolve a empresa (ou o super-admin).
  { path: 'login', component: Login, title: 'Entrar · eTrace' },
  { path: 'solicitar-acesso', component: RequestAccess, title: 'Solicitar acesso · eTrace' },

  // Plataforma (super-admin)
  { path: 'platform', canActivate: [platformAuthGuard], component: AccessRequests, title: 'Plataforma · Solicitações' },

  // O aceite de convite continua por tenant: o link enviado já traz o slug.
  { path: ':tenant/aceitar-convite', component: AcceptInvite, title: 'Ativar acesso · eTrace' },

  // Portal público do site (sem login): registo de ocorrências + acompanhamento anónimo por código.
  { path: ':tenant/canal-denuncias', component: CanalDenuncias, title: 'Canal de denúncias · eTrace' },
  { path: ':tenant/participar', component: OccurrencePublicForm, title: 'Registar ocorrência · eTrace' },
  { path: ':tenant/acompanhar', component: OccurrencePublicTracking, title: 'Acompanhar ocorrência · eTrace' },
  // Conversa externa: o link que segue em cada e-mail enviado da ocorrência.
  { path: ':tenant/conversa/:token', component: OccurrenceConversation, title: 'Conversa · eTrace' },

  // Aplicação do tenant (protegida)
  {
    path: ':tenant',
    component: Layout,
    canActivate: [tenantAuthGuard],
    children: [
      { path: '', redirectTo: 'ocorrencias', pathMatch: 'full' },
      { path: 'dashboard', component: Dashboard, title: 'Início · eTrace' },
      // O formulário é filho da lista e do detalhe, e não uma rota irmã: assim o que está por
      // baixo continua montado enquanto o painel está aberto. É isso que dá sentido a minimizar
      // — encostado ao canto, vê-se a lista (ou a ocorrência) por trás, e volta-se ao painel com
      // o que já lá estava escrito. As URLs são as mesmas de sempre.
      {
        path: 'ocorrencias',
        component: OccurrenceList,
        title: 'Ocorrências · eTrace',
        children: [
          {
            path: 'nova',
            component: OccurrenceForm,
            title: 'Nova ocorrência · eTrace',
            data: { overlay: true },
            canActivate: [permissionGuard('occurrence:write')],
            canDeactivate: [(form: OccurrenceForm) => form.canLeave()],
          },
        ],
      },
      {
        path: 'ocorrencias/:id',
        component: OccurrenceDetail,
        title: 'Ocorrência · eTrace',
        children: [
          {
            path: 'editar',
            component: OccurrenceForm,
            title: 'Editar ocorrência · eTrace',
            data: { overlay: true },
            canActivate: [permissionGuard('occurrence:write')],
            canDeactivate: [(form: OccurrenceForm) => form.canLeave()],
          },
        ],
      },
      { path: 'casos-criticos', component: CriticalCaseList, canActivate: [criticalCaseGuard], title: 'Casos críticos · eTrace' },
      { path: 'casos-criticos/:id', component: CriticalCaseDetail, canActivate: [criticalCaseGuard], title: 'Caso crítico · eTrace' },
      { path: 'estatisticas', component: OccurrenceStatistics, title: 'Estatísticas · eTrace' },
      { path: 'clientes', component: ClientList, title: 'Clientes · eTrace' },
      { path: 'clientes/novo', component: ClientForm, canActivate: [permissionGuard('client:write')], title: 'Novo cliente · eTrace' },
      { path: 'clientes/:id', component: ClientDetail, title: 'Cliente · eTrace' },
      { path: 'clientes/:id/editar', component: ClientForm, canActivate: [permissionGuard('client:write')], title: 'Editar cliente · eTrace' },
      { path: 'organizacoes', component: OrganizationList, title: 'Organizações · eTrace' },
      { path: 'organizacoes/nova', component: OrganizationForm, canActivate: [permissionGuard('organization:write')], title: 'Nova organização · eTrace' },
      { path: 'organizacoes/:id', component: OrganizationDetail, title: 'Organização · eTrace' },
      { path: 'organizacoes/:id/editar', component: OrganizationForm, canActivate: [permissionGuard('organization:write')], title: 'Editar organização · eTrace' },
      { path: 'configuracoes', canActivate: [permissionGuard('user:write')], component: Settings, title: 'Configurações · eTrace' },
      { path: 'configuracoes/usuarios', canActivate: [permissionGuard('user:write')], component: UserList, title: 'Utilizadores · eTrace' },
      { path: 'configuracoes/portal', canActivate: [permissionGuard('user:write')], component: PublicPortal, title: 'Portal público · eTrace' },
      { path: 'configuracoes/caixas', canActivate: [permissionGuard('user:write')], component: ModuleMailboxes, title: 'Caixas de e-mail · eTrace' },
      { path: 'configuracoes/modelos-email', canActivate: [permissionGuard('user:write')], component: MailTemplates, title: 'Modelos de e-mail · eTrace' },
      // Envio de e-mail (MailSettings): fora das rotas por agora. As credenciais do Graph vêm do
      // pipeline e servem toda a instalação; o ecrã volta quando houver uma organização com
      // Microsoft 365 próprio. O componente e os endpoints ficam no sítio, à espera disso.
    ],
  },

  { path: '**', redirectTo: 'login' },
];
